import { RentalManagerListingCriteriaType } from '@zg-rentals/ts-rental-manager-api-beta-client';

import numberUtils from '../utils/numberUtils';
import stringHelper from '../utils/stringHelper';

export const NOT_APPLICABLE = 'Not applicable';
export const NOT_SET = 'Not set';

export const STRING_VALUE_TO_NUMBER_VALUE_MAP = {
  '': 1,
  [NOT_APPLICABLE]: 1,
  '1.5x': 1.5,
  '1.75x': 1.75,
  '2x': 2,
  '2.25x': 2.25,
  '2.5x': 2.5,
  '2.75x': 2.75,
  '3x': 3,
  '3.25x': 3.25,
  '3.5x': 3.5,
  '3.75x': 3.75,
  '4x': 4,
} as Record<string, number>;

export const LC_MIN_INCOME_TO_RENT_RATIO_OPTIONS = [
  { label: NOT_SET, value: NOT_APPLICABLE },
  { label: '1.5x', value: '1.5x' },
  { label: '1.75x', value: '1.75x' },
  { label: '2x', value: '2x' },
  { label: '2.25x', value: '2.25x' },
  { label: '2.5x', value: '2.5x' },
  { label: '2.75x', value: '2.75x' },
  { label: '3x', value: '3x' },
  { label: '3.25x', value: '3.25x' },
  { label: '3.5x', value: '3.5x' },
  { label: '3.75x', value: '3.75x' },
  { label: '4x', value: '4x' },
];

export const LC_MIN_CREDIT_SCORE_OPTIONS = [
  { label: NOT_SET, value: NOT_APPLICABLE },
  // 500 - 735 with interval of 5
  ...Array.from({ length: (735 - 500) / 5 + 1 }, (_, i) => {
    const value = (500 + i * 5).toString();
    return { label: value, value };
  }),
  { label: '740+', value: '740+' },
];

const CAT_COUNT_VALUES = {
  NO_CATS: 'Not allowed',
  ONE_CAT: '1 cat',
  TWO_CATS: '2 cats',
  THREE_PLUS_CATS: '3+ cats',
} as const;

const DOG_COUNT_VALUES = {
  NO_DOGS: 'Not allowed',
  ONE_DOG: '1 dog',
  TWO_DOGS: '2 dogs',
  THREE_PLUS_DOGS: '3+ dogs',
} as const;

const DOG_SIZE_VALUES = {
  '': undefined,
  SMALL: 'Small dogs - less than 20 lbs',
  MEDIUM: 'Medium dogs - less than 40 lbs',
  LARGE: 'Large dogs - over 40 lbs',
} as const;

export const THREE_PLUS = '3+';

export const LC_PET_POLICY_FORM_CONSTANTS = {
  LABEL_TO_VALUE_MAP: {
    '': '',
    [CAT_COUNT_VALUES.NO_CATS]: '0',
    [CAT_COUNT_VALUES.ONE_CAT]: '1',
    [CAT_COUNT_VALUES.TWO_CATS]: '2',
    [CAT_COUNT_VALUES.THREE_PLUS_CATS]: THREE_PLUS,
    [DOG_COUNT_VALUES.ONE_DOG]: '1',
    [DOG_COUNT_VALUES.TWO_DOGS]: '2',
    [DOG_COUNT_VALUES.THREE_PLUS_DOGS]: THREE_PLUS,
    [DOG_SIZE_VALUES.SMALL]: 'small',
    [DOG_SIZE_VALUES.MEDIUM]: 'medium',
    [DOG_SIZE_VALUES.LARGE]: 'large',
  },
  VALUE_TO_LABEL_MAP: {
    petPolicyCatCount0: CAT_COUNT_VALUES.NO_CATS,
    petPolicyCatCount1: CAT_COUNT_VALUES.ONE_CAT,
    petPolicyCatCount2: CAT_COUNT_VALUES.TWO_CATS,
    'petPolicyCatCount3+': CAT_COUNT_VALUES.THREE_PLUS_CATS,
    petPolicyCatCount: '',
    petPolicyDogCount0: DOG_COUNT_VALUES.NO_DOGS,
    petPolicyDogCount1: DOG_COUNT_VALUES.ONE_DOG,
    petPolicyDogCount2: DOG_COUNT_VALUES.TWO_DOGS,
    'petPolicyDogCount3+': DOG_COUNT_VALUES.THREE_PLUS_DOGS,
    petPolicyDogCount: '',
    petPolicyDogSizesmall: DOG_SIZE_VALUES.SMALL,
    petPolicyDogSizemedium: DOG_SIZE_VALUES.MEDIUM,
    petPolicyDogSizelarge: DOG_SIZE_VALUES.LARGE,
    petPolicyDogSize: '',
  },
  CAT_COUNT_OPTIONS: Object.values(CAT_COUNT_VALUES),
  DOG_COUNT_OPTIONS: Object.values(DOG_COUNT_VALUES),
  DOG_SIZE_OPTIONS: Object.values(DOG_SIZE_VALUES).filter((value) => value !== undefined),
};

export const TYPE_MAP_DISPLAY_VALUE_LC_PET_POLICY = {
  [RentalManagerListingCriteriaType.PET_POLICY_CAT_COUNT]: {
    label: 'Cats',
    getValue: (catCount: string, _: string) => {
      switch (catCount) {
        case '0':
          return 'Not allowed';
        case '':
          return 'Allowed';
        case undefined:
          return 'Allowed';
        default:
          return catCount + stringHelper.pluralizeWord(Number(catCount[0]), ' cat', ' cats');
      }
    },
  },
  [RentalManagerListingCriteriaType.PET_POLICY_DOG_COUNT]: {
    label: 'Dogs',
    getValue: (dogCount: string, dogSize: string) => {
      if (dogCount === '0') {
        return 'Not allowed';
      }

      const dogSizeValue = DOG_SIZE_VALUES[(dogSize ? dogSize.toUpperCase() : dogSize) as keyof typeof DOG_SIZE_VALUES];

      const dogSizeSubtext = dogSizeValue?.split('-')?.pop()?.trim() || '';

      if (dogCount === '' || dogCount === undefined) {
        return dogSizeValue || 'Allowed';
      }

      return `${dogCount}${stringHelper.pluralizeWord(
        Number(dogCount[0]),
        ` ${dogSize ? dogSize + ' ' : ''}dog`,
        ` ${dogSize ? dogSize + ' ' : ''}dogs`,
      )}${dogSizeSubtext ? ` (${dogSizeSubtext})` : ''}`;
    },
  },
  [RentalManagerListingCriteriaType.PET_POLICY_SMALL_DOG_COUNT]: {
    label: 'Small Dogs',
    getValue: (dogCount: string, dogSize: string) => {
      if (dogCount === '0') {
        return 'Not allowed';
      }

      const dogSizeValue = DOG_SIZE_VALUES[(dogSize ? dogSize.toUpperCase() : dogSize) as keyof typeof DOG_SIZE_VALUES];

      const dogSizeSubtext = dogSizeValue?.split('-')?.pop()?.trim() || '';

      if (dogCount === '' || dogCount === undefined) {
        return dogSizeValue || 'Allowed';
      }

      return `${dogCount}${stringHelper.pluralizeWord(
        Number(dogCount[0]),
        ` ${dogSize ? dogSize + ' ' : ''}dog`,
        ` ${dogSize ? dogSize + ' ' : ''}dogs`,
      )}${dogSizeSubtext ? ` (${dogSizeSubtext})` : ''}`;
    },
  },
  [RentalManagerListingCriteriaType.PET_POLICY_LARGE_DOG_COUNT]: {
    label: 'Large Dogs',
    getValue: (dogCount: string, dogSize: string) => {
      if (dogCount === '0') {
        return 'Not allowed';
      }

      const dogSizeValue = DOG_SIZE_VALUES[(dogSize ? dogSize.toUpperCase() : dogSize) as keyof typeof DOG_SIZE_VALUES];

      const dogSizeSubtext = dogSizeValue?.split('-')?.pop()?.trim() || '';

      if (dogCount === '' || dogCount === undefined) {
        return dogSizeValue || 'Allowed';
      }

      return `${dogCount}${stringHelper.pluralizeWord(
        Number(dogCount[0]),
        ` ${dogSize ? dogSize + ' ' : ''}dog`,
        ` ${dogSize ? dogSize + ' ' : ''}dogs`,
      )}${dogSizeSubtext ? ` (${dogSizeSubtext})` : ''}`;
    },
  },
  [RentalManagerListingCriteriaType.PET_POLICY_PET_DEPOSIT]: {
    label: 'Pet deposit',
    getValue: (petDeposit: string, _: string) => numberUtils.asCurrency(Number(petDeposit)),
  },
  [RentalManagerListingCriteriaType.PET_POLICY_PET_RENT]: {
    label: 'Pet rent',
    getValue: (petRent: string, _: string) => numberUtils.asCurrency(Number(petRent)),
  },
  negotiable: {
    label: 'Flexibility',
    getValue: (negotiable: boolean | null, fallback: string) => {
      if (negotiable == null) {
        return fallback;
      }

      return negotiable ? 'Negotiable' : 'Non-negotiable';
    },
  },
} as const;
